import React, { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { IconWarning } from '../../../../../assets/icons/icon-warning';
import { CircularProgress, CustomBackdrop, EmptyStateBox } from '../../../../../components';
import { useInstallationContext } from '../../../../../context/installation';
import { ROUTES } from '../../../../../routes/routes';
import { useDevicesAndChannels } from '../../../hooks/devices-and-channels';
import { useChannelsStore } from '../../../store/channels';
import { useDevicesStore } from '../../../store/devices';
import { useGroupsStore } from '../../../store/groups';
import { useAddDevice } from '../../device-add/hooks/use-add-device';

export enum PageType {
  Channels = 'Channels',
  Groups = 'Groups',
  Devices = 'Devices',
}

type CheckContentWrapperProps = {
  children: ReactNode;
  page?: PageType;
};

const CheckContentWrapper: React.FC<CheckContentWrapperProps> = ({ children, page }) => {
  const { t } = useTranslation('installation');
  const { t: tch } = useTranslation('channel-list');
  const { t: td } = useTranslation('device-list');
  const {
    selectedInstallationId,
    installationList,
    installationsFetched,
    installationsLoading,
    currentInstallationLoading,
  } = useInstallationContext();
  const channelsLoading = useChannelsStore((s) => s.channelsLoading);
  const devicesLoading = useDevicesStore((s) => s.devicesLoading);
  const groupsLoading = useGroupsStore((s) => s.groupsLoading);
  const { addDevice } = useAddDevice();
  const { channelGroups, deviceList, channelList } = useDevicesAndChannels();
  const loading =
    currentInstallationLoading || installationsLoading || channelsLoading || devicesLoading || groupsLoading;

  const renderContent = () => {
    if (loading) return <CircularProgress />;

    if (installationsFetched && installationList.length === 0)
      return (
        <EmptyStateBox
          header={t('installation')}
          content={t('noInstallations.message')}
          btnText={t('noInstallations.button')}
          icon={<IconWarning />}
          linkTo={ROUTES.InstallationCreate()}
          showAllways
        />
      );

    if (!selectedInstallationId)
      return (
        <EmptyStateBox
          header={t('installation')}
          content={t('noSelectedInstallation.message')}
          btnText={t('noSelectedInstallation.button')}
          icon={<IconWarning />}
          linkTo={ROUTES.InstallationCreate()}
          showAllways
        />
      );

    if (page === PageType.Channels && channelGroups.length === 0)
      return (
        <EmptyStateBox
          header={tch('devicesEmptyState.header')}
          btnText={tch('devicesEmptyState.missingDevice.btnText')}
          content={tch('devicesEmptyState.missingDevice.content')}
          icon={<IconWarning />}
          onClick={addDevice}
        />
      );

    if (page === PageType.Groups && channelList.length === 0)
      return (
        <EmptyStateBox
          header={tch('devicesEmptyState.header')}
          btnText={tch('devicesEmptyState.missingDevice.btnText')}
          content={tch('devicesEmptyState.missingDevice.content')}
          icon={<IconWarning />}
          onClick={addDevice}
        />
      );

    if (page === PageType.Devices && deviceList.length === 0)
      return (
        <EmptyStateBox
          header={td('devicesEmptyState.header')}
          content={td('devicesEmptyState.content')}
          icon={<IconWarning />}
        />
      );

    return children;
  };

  return (
    <>
      {renderContent()}
      <CustomBackdrop loading={loading} />
    </>
  );
};

export default CheckContentWrapper;
